import React, { useCallback, useEffect, useMemo, useState } from 'react';
// Components
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  FileFormatIcon,
  Flex,
  ListItem,
  ScrollArea,
  Switch,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconBinaryTree,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCornerDownRight,
  IconFileFilled,
  IconFolderOpen,
  IconInfoCircle,
  IconList,
} from '@tabler/icons-react';
import clsx from 'clsx';

// Types
import {
  ColumnMapping,
  FileToFormMapping,
  SelectedFile,
  StudyForm,
} from './types';

// Utils
import {
  buildFormTree,
  FormTreeNode,
  matchesNestedPath,
  buildNestedPathFromIds,
} from './utils/formTreeUtils';

// Components
import { FormIcon } from './FormIcon';
import { HoverCard } from '@radix-ui/themes';
import { StudyFormHoverCard } from '@/components/data-import/StudyFormHoverCard';
import { CsvFileHoverCard } from '@/components/data-import/CsvFileHoverCard';

// Styles
import './FormSelector.css';

interface FormHierarchyPreviewTreeProps {
  forms: StudyForm[];
  fileToFormMappings: FileToFormMapping[];
  selectedFiles: SelectedFile[];
  columnMappings: ColumnMapping[];
  selectedFormId?: number;
  selectedFileId?: string;
  selectedFormNestedPath?: string;
  onFormSelect: (formId: number, nestedPath?: string) => void;
  onFileSelect: (fileId: string) => void;
}

interface TreeNodeProps {
  form: FormTreeNode;
  files: Array<{
    file: SelectedFile;
    fileId: string;
    mappings: ColumnMapping[];
    nestedPath?: string;
  }>;
  level: number;
  pathFromRoot?: number[]; // Path from root to this node (array of form IDs)
  selectedFormId?: number;
  selectedFileId?: string;
  selectedFormNestedPath?: string;
  onFormSelect: (formId: number, nestedPath?: string) => void;
  onFileSelect: (fileId: string) => void;
  filesByForm: Map<
    number,
    Array<{
      file: SelectedFile;
      fileId: string;
      mappings: ColumnMapping[];
      nestedPath?: string;
    }>
  >;
  forms: StudyForm[];
  formsMap: Map<number, StudyForm>;
  expandedNodes: Set<number>;
  onToggleExpand: (formId: number) => void;
}

type ViewMode = 'all-files' | 'form-tree-with-files';

// Get display name from json_schema or fallback to name
const getDisplayName = (form: StudyForm | FormTreeNode) => {
  if (form.json_schema?.displayName) {
    return form.json_schema.displayName;
  }
  return form.name;
};

const TreeNode: React.FC<TreeNodeProps> = ({
  form,
  files,
  level,
  pathFromRoot = [],
  selectedFormId,
  selectedFileId,
  selectedFormNestedPath,
  onFormSelect,
  onFileSelect,
  filesByForm,
  forms,
  formsMap,
  expandedNodes,
  onToggleExpand,
}) => {
  const hasChildren = form.children.length > 0;
  const isExpanded = expandedNodes.has(form.id);

  // Build current path from root including this node
  const currentPath = useMemo(() => [...pathFromRoot, form.id], [pathFromRoot, form.id]);

  // Filter files to show only those that match the current path
  // If a file has nested_path, it must match the current path
  // If a file doesn't have nested_path (old mappings), show it for backward compatibility
  const filteredFiles = useMemo(() => {
    return files.filter(fileData => {
      // If no nested_path, show the file (backward compatibility)
      if (!fileData.nestedPath) {
        return true;
      }
      // If nested_path exists, check if it matches the current path
      return matchesNestedPath(currentPath, fileData.nestedPath);
    });
  }, [files, currentPath]);

  // Check if this form is selected
  // If selectedFormNestedPath exists, compare the path to ensure we highlight only the correct path
  // when a form has multiple parents. Otherwise, fall back to simple ID comparison.
  const isSelected = useMemo(() => {
    if (selectedFormId !== form.id) return false;
    
    // If selectedFormNestedPath is provided, check if current path matches
    if (selectedFormNestedPath) {
      return matchesNestedPath(currentPath, selectedFormNestedPath);
    }
    
    // If no selectedFormNestedPath, check files with nested_path
    const filteredFilesWithNestedPath = filteredFiles.filter(fileData => fileData.nestedPath);
    
    // If there are files with nested_path, we must check the path
    if (filteredFilesWithNestedPath.length > 0) {
      // filteredFiles already contains only files that match the current path
      // So if filteredFiles has files with nested_path, it means there are files matching this path
      // If selectedFileId is set, also check that the selected file is in filteredFiles
      if (selectedFileId) {
        return filteredFiles.some(fileData => fileData.fileId === selectedFileId);
      }
      
      // If no selectedFileId, check if there are any files matching the current path
      return filteredFiles.length > 0;
    }
    
    // Check if there are any files with nested_path in all files (for this form)
    // If yes, but none match current path, don't highlight
    const allFilesWithNestedPath = files.filter(fileData => fileData.nestedPath);
    if (allFilesWithNestedPath.length > 0) {
      // There are files with nested_path, but none match current path - don't highlight
      return false;
    }
    
    // Fallback: if no files have nested_path, show as selected (backward compatibility)
    return true;
  }, [selectedFormId, form.id, currentPath, selectedFormNestedPath, filteredFiles, files, selectedFileId]);

  const handleFormClick = useCallback(() => {
    // Build nested_path from current path
    const nestedPath = buildNestedPathFromIds(currentPath, forms);
    onFormSelect(form.id, nestedPath);
  }, [form.id, currentPath, forms, onFormSelect]);

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand(form.id);
    },
    [form.id, onToggleExpand]
  );

  const formFieldCount = form.fields?.length || 0;
  const _mappedFieldsCount = filteredFiles.reduce((count, fileData) => {
    return (
      count +
      fileData.mappings.filter(m => m.mappingType === 'formField').length
    );
  }, 0);

  const indentStyle = {
    width: '100%',
    '--tree-level': level || 0,
  };

  return (
    <div className={clsx('treeNode')}>
      {/* Form Node */}
      <div
        className={clsx('nodeContent', 'formNode', {
          ['selected']: isSelected,
        })}
        style={
          {
            ['--list-level']: level,
            paddingLeft: `${level * 20 + 8}px`,
          } as React.CSSProperties & { [key: string]: number | string }
        }
      >
        <Flex align="center" gap="2" justify="between" width="100%">
          <Flex
            align="center"
            gap="1"
            style={{ flex: 1, cursor: 'pointer', position: 'relative' }}
            onClick={handleFormClick}
          >
            {/* Expand/Collapse button */}
            {/*{hasChildren && (*/}
            <Button
              variant="ghost"
              color="secondary"
              size="xsmall"
              isIconOnly
              disabled={!hasChildren}
              onClick={handleToggleExpand}
              className={clsx('expandButton', {
                hasChildren,
              })}
            >
              {isExpanded ? <IconChevronDown /> : <IconChevronRight />}
            </Button>
            {/*)}*/}

            {/* Form icon with tree indicator */}
            <ListItem
              style={indentStyle}
              className="form-selector-tree-item"
              selected={isSelected}
              startIcon={
                <>
                  {level > 0 && (
                    <div className="form-selector-tree-indicator" />
                  )}

                  <FormIcon form={form} availableForms={forms} />
                </>
              }
              primary={
                <Flex align="center" gap="2">
                  <Text weight="medium" size="2xsmall">
                    {form.name}
                  </Text>
                  <VisuallyHidden>
                    <Text
                      size="3xsmall"
                      color="gray"
                      className="form-selector-subform-display-name"
                    >
                      [{form.fieldCount} fields]
                    </Text>
                  </VisuallyHidden>
                </Flex>
              }
              secondary={
                <VisuallyHidden>
                  <Text size="3xsmall" color="primary">
                    {getDisplayName(form)}
                  </Text>
                </VisuallyHidden>
              }
              endIcon={
                <>
                  {/* Files under this form - in separate container */}
                  {filteredFiles.length > 0 && (
                    <HoverCard.Root>
                      <HoverCard.Trigger>
                        <Button
                          size="xsmall"
                          variant="surface"
                          color="success"
                          mr="2"
                        >
                          +{filteredFiles.length}
                        </Button>
                      </HoverCard.Trigger>
                      <HoverCard.Content onClick={e => e.stopPropagation()}>
                        <>
                          <Text size="3xsmall" mb="2">
                            Mapped files:
                          </Text>
                          {filteredFiles.length > 0 && (
                            <Flex direction="column" className={'filesList'}>
                              {filteredFiles.map(({ file, fileId, mappings }) => {
                                const fileName =
                                  file.source === 'uploaded'
                                    ? file.file!.name
                                    : file.csvFile!.filename;
                                const isFileSelected =
                                  selectedFileId === fileId;
                                const metadataMappings = mappings.filter(
                                  m => m.mappingType === 'metadata'
                                ).length;
                                const formFieldMappings = mappings.filter(
                                  m => m.mappingType === 'formField'
                                ).length;
                                const ignoredMappings = mappings.filter(
                                  m => m.mappingType === 'ignored'
                                ).length;

                                return (
                                  <div
                                    key={fileId}
                                    className={clsx('fileNode', {
                                      ['selected']: isFileSelected,
                                    })}
                                    onClick={() => onFileSelect(fileId)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <ListItem
                                      style={{
                                        width: '100%',
                                      }}
                                      className={'isFile'}
                                      selected={isFileSelected}
                                      startIcon={
                                        <>
                                          <IconFileFilled />

                                          <VisuallyHidden>
                                            <FileFormatIcon
                                              fileName={fileName}
                                              mimeType="text/csv"
                                              size="xsmall"
                                            />
                                          </VisuallyHidden>
                                        </>
                                      }
                                      primary={
                                        <Text size="2xsmall">{fileName}</Text>
                                      }
                                      endIcon={
                                        file.csvFile ? (
                                          <CsvFileHoverCard
                                            file={file.csvFile}
                                            columns={file.metadata.columnCount}
                                            rows={file.metadata.rowCount}
                                          >
                                            <Button
                                              isIconOnly
                                              size="xsmall"
                                              variant="ghost"
                                            >
                                              <IconInfoCircle />
                                            </Button>
                                          </CsvFileHoverCard>
                                        ) : (
                                          <HoverCard.Root>
                                            <HoverCard.Trigger>
                                              <Button
                                                isIconOnly
                                                size="xsmall"
                                                variant="ghost"
                                              >
                                                <IconInfoCircle />
                                              </Button>
                                            </HoverCard.Trigger>
                                            <HoverCard.Content>
                                              <Flex gap="1" wrap="wrap">
                                                {metadataMappings > 0 && (
                                                  <Badge
                                                    size="xsmall"
                                                    variant="soft"
                                                    color="blue"
                                                  >
                                                    {metadataMappings} meta
                                                  </Badge>
                                                )}
                                                {formFieldMappings > 0 && (
                                                  <Badge
                                                    size="xsmall"
                                                    variant="soft"
                                                    color="green"
                                                  >
                                                    {formFieldMappings} fields
                                                  </Badge>
                                                )}
                                                {ignoredMappings > 0 && (
                                                  <Badge
                                                    size="xsmall"
                                                    variant="soft"
                                                    color="orange"
                                                  >
                                                    {ignoredMappings} ignored
                                                  </Badge>
                                                )}
                                                <Badge
                                                  size="xsmall"
                                                  variant="outlined"
                                                >
                                                  {file.metadata.rowCount.toLocaleString()}{' '}
                                                  rows
                                                </Badge>
                                              </Flex>
                                            </HoverCard.Content>
                                          </HoverCard.Root>
                                        )
                                      }
                                    />
                                  </div>
                                );
                              })}
                            </Flex>
                          )}
                        </>
                      </HoverCard.Content>
                    </HoverCard.Root>
                  )}
                  {formsMap.has(form.id) && (
                    <StudyFormHoverCard
                      form={formsMap.get(form.id)!}
                      fieldCount={formFieldCount}
                      allForms={formsMap}
                    >
                      <Button
                        isIconOnly
                        size="xsmall"
                        variant="ghost"
                        className="formInfoButton"
                      >
                        <IconInfoCircle />
                      </Button>
                    </StudyFormHoverCard>
                  )}
                </>
              }
            />
          </Flex>
        </Flex>
      </div>

      {/* Child forms */}
      {hasChildren && isExpanded && (
        <div className={'form-selector-tree-children-group'}>
          {form.children.map(childForm => {
            // Get all files for this child form
            const allChildFiles = filesByForm.get(childForm.id) || [];
            // Build the path for this child form
            const childPath = [...currentPath, childForm.id];
            // Filter files to only include those matching the child path
            const childFiles = allChildFiles.filter(fileData => {
              // If no nested_path, include the file (backward compatibility)
              if (!fileData.nestedPath) {
                return true;
              }
              // If nested_path exists, check if it matches the child path
              return matchesNestedPath(childPath, fileData.nestedPath);
            });

            return (
              <TreeNode
                key={childForm.id}
                form={childForm}
                files={childFiles}
                level={level + 1}
                pathFromRoot={currentPath}
                selectedFormId={selectedFormId}
                selectedFileId={selectedFileId}
                selectedFormNestedPath={selectedFormNestedPath}
                onFormSelect={onFormSelect}
                onFileSelect={onFileSelect}
                filesByForm={filesByForm}
                forms={forms}
                formsMap={formsMap}
                expandedNodes={expandedNodes}
                onToggleExpand={onToggleExpand}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const FormHierarchyPreviewTree: React.FC<
  FormHierarchyPreviewTreeProps
> = ({
  forms,
  fileToFormMappings,
  selectedFiles,
  columnMappings,
  selectedFormId,
  selectedFileId,
  selectedFormNestedPath,
  onFormSelect,
  onFileSelect,
}) => {
  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('form-tree-with-files');
  // Filter state - enabled by default to show only forms with imports
  const [showOnlyImportForms, setShowOnlyImportForms] = useState(true);
  // Expanded nodes state
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  // Toggle expand/collapse for a form
  const handleToggleExpand = useCallback((formId: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(formId)) {
        next.delete(formId);
      } else {
        next.add(formId);
      }
      return next;
    });
  }, []);

  // Auto-expand first 2 levels on mount
  useEffect(() => {
    const formTreeRoots = buildFormTree(forms);
    const newExpanded = new Set<number>();

    const expandToLevel = (nodes: FormTreeNode[], level: number) => {
      nodes.forEach(node => {
        if (level < 2) {
          newExpanded.add(node.id);
          expandToLevel(node.children, level + 1);
        }
      });
    };

    expandToLevel(formTreeRoots, 0);
    setExpandedNodes(newExpanded);
  }, [forms]);

  // Group files by form, storing nested_path for each file
  const filesByForm = useMemo(() => {
    const map = new Map<
      number,
      Array<{
        file: SelectedFile;
        fileId: string;
        mappings: ColumnMapping[];
        nestedPath?: string; // Store nested_path for path matching
      }>
    >();

    selectedFiles.forEach(file => {
      const fileId =
        file.source === 'uploaded'
          ? file.file!.name
          : `csv-${file.csvFile!.id}`;
      const formMapping = fileToFormMappings.find(m => m.fileId === fileId);
      const formId = formMapping?.formId;

      if (formId) {
        if (!map.has(formId)) {
          map.set(formId, []);
        }
        map.get(formId)!.push({
          file,
          fileId,
          mappings: columnMappings.filter(cm => cm.fileId === fileId),
          nestedPath: formMapping?.nested_path,
        });
      }
    });

    return map;
  }, [selectedFiles, fileToFormMappings, columnMappings]);

  // Create forms map for StudyFormHoverCard
  const formsMap = useMemo(() => {
    const map = new Map<number, StudyForm>();
    forms.forEach(form => {
      map.set(form.id, form);
    });
    return map;
  }, [forms]);

  // Build hierarchical structure using formTreeUtils (subFormArray logic)
  const formTreeRoots = useMemo(() => {
    return buildFormTree(forms);
  }, [forms]);

  // Get all forms that have mappings or are ancestors of forms with mappings
  const formsWithMappings = useMemo(() => {
    const formsWithDirectMappings = new Set<number>();

    // Find forms with direct file mappings
    filesByForm.forEach((files, formId) => {
      if (files.length > 0) {
        formsWithDirectMappings.add(formId);
      }
    });

    // Find ancestors of forms with mappings
    const allFormsWithMappings = new Set(formsWithDirectMappings);

    const findAncestors = (node: FormTreeNode) => {
      if (allFormsWithMappings.has(node.id)) {
        // Mark all ancestors as having mappings
        let current: FormTreeNode | undefined = node;
        while (current?.parent) {
          allFormsWithMappings.add(current.parent.id);
          current = current.parent;
        }
      }
      node.children.forEach(findAncestors);
    };

    formTreeRoots.forEach(findAncestors);

    return allFormsWithMappings;
  }, [filesByForm, formTreeRoots]);

  // Extract root forms and apply filters
  const rootForms = useMemo(() => {
    let roots = formTreeRoots.map(root => root);

    // Apply filter if enabled
    if (showOnlyImportForms) {
      roots = roots.filter(root => {
        // Check if root form or any of its descendants have mappings
        const hasMappings = (node: FormTreeNode): boolean => {
          if (formsWithMappings.has(node.id)) return true;
          return node.children.some(child => hasMappings(child));
        };

        return hasMappings(root);
      });
    }

    return roots;
  }, [formTreeRoots, showOnlyImportForms, formsWithMappings]);

  // Render tree view
  const renderTreeView = () => (
    <Flex direction="column" className={'treeContainer'}>
      {rootForms.map(form => {
        // Get all files for this root form
        const allRootFiles = filesByForm.get(form.id) || [];
        // Build the path for this root form (just the root form itself)
        const rootPath = [form.id];
        // Filter files to only include those matching the root path
        const rootFiles = allRootFiles.filter(fileData => {
          // If no nested_path, include the file (backward compatibility)
          if (!fileData.nestedPath) {
            return true;
          }
          // If nested_path exists, check if it matches the root path
          return matchesNestedPath(rootPath, fileData.nestedPath);
        });

        return (
          <TreeNode
            key={form.id}
            form={form}
            files={rootFiles}
            level={0}
            pathFromRoot={[]}
            selectedFormId={selectedFormId}
            selectedFileId={selectedFileId}
            selectedFormNestedPath={selectedFormNestedPath}
            onFormSelect={onFormSelect}
            onFileSelect={onFileSelect}
            filesByForm={filesByForm}
            forms={forms}
            formsMap={formsMap}
            expandedNodes={expandedNodes}
            onToggleExpand={handleToggleExpand}
          />
        );
      })}
    </Flex>
  );

  // Render all files view
  const renderAllFilesView = () => (
    <Flex direction="column" gap="2" className={'treeContainer'}>
      {selectedFiles.map(file => {
        const fileId =
          file.source === 'uploaded'
            ? file.file!.name
            : `csv-${file.csvFile!.id}`;
        const formMapping = fileToFormMappings.find(m => m.fileId === fileId);
        const formId = formMapping?.formId;
        const form = formId ? forms.find(f => f.id === formId) : null;
        const mappings = columnMappings.filter(cm => cm.fileId === fileId);
        const isFileSelected = selectedFileId === fileId;
        const fileName =
          file.source === 'uploaded' ? file.file!.name : file.csvFile!.filename;

        const metadataMappings = mappings.filter(
          m => m.mappingType === 'metadata'
        ).length;
        const formFieldMappings = mappings.filter(
          m => m.mappingType === 'formField'
        ).length;
        const ignoredMappings = mappings.filter(
          m => m.mappingType === 'ignored'
        ).length;

        return (
          <div
            key={fileId}
            className={clsx('fileNode', {
              ['selected']: isFileSelected,
            })}
            onClick={() => onFileSelect(fileId)}
            style={{ cursor: 'pointer' }}
          >
            <ListItem
              className={'isFile'}
              selected={isFileSelected}
              startIcon={
                <FileFormatIcon
                  fileName={fileName}
                  mimeType="text/csv"
                  size="xsmall"
                  // showIcon={true}
                />
              }
              primary={<Text size="2xsmall">{fileName}</Text>}
              secondary={
                form ? (
                  <Text size="3xsmall" color="primary">
                    <IconCornerDownRight /> {getDisplayName(form)}
                  </Text>
                ) : null
              }
              endIcon={
                file.csvFile ? (
                  <CsvFileHoverCard
                    file={file.csvFile}
                    columns={file.metadata.columnCount}
                    rows={file.metadata.rowCount}
                  >
                    <Button isIconOnly size="xsmall" variant="ghost">
                      <IconInfoCircle />
                    </Button>
                  </CsvFileHoverCard>
                ) : (
                  <HoverCard.Root>
                    <HoverCard.Trigger>
                      <Button isIconOnly size="xsmall" variant="ghost">
                        <IconInfoCircle />
                      </Button>
                    </HoverCard.Trigger>
                    <HoverCard.Content>
                      <Flex gap="1" wrap="wrap">
                        {metadataMappings > 0 && (
                          <Badge size="xsmall" variant="soft" color="blue">
                            {metadataMappings} meta
                          </Badge>
                        )}
                        {formFieldMappings > 0 && (
                          <Badge size="xsmall" variant="soft" color="green">
                            {formFieldMappings} fields
                          </Badge>
                        )}
                        {ignoredMappings > 0 && (
                          <Badge size="xsmall" variant="soft" color="orange">
                            {ignoredMappings} ignored
                          </Badge>
                        )}
                        <Badge size="xsmall" variant="outlined">
                          {file.metadata.rowCount.toLocaleString()} rows
                        </Badge>
                      </Flex>
                    </HoverCard.Content>
                  </HoverCard.Root>
                )
              }
            />
          </div>
        );
      })}
    </Flex>
  );

  return (
    <ScrollArea type="auto">
      <Flex p="4" direction="column" gap="3" className="importDataTree">
        <Flex align="center" justify="between">
          <Flex align="center" gap="2" mr="auto">
            <IconFolderOpen />
            <Text size="2xsmall" weight="medium">
              Form Structure & Files
            </Text>
            <Tooltip
              content={
                viewMode === 'all-files'
                  ? 'All files are shown in a flat list. Click on a file to preview its data.'
                  : showOnlyImportForms
                    ? 'Showing only forms with imports and their parent forms. Use the dropdown menu to change the view or filter.'
                    : 'Click on forms or files to preview data. Files are grouped under their target forms.'
              }
            >
              <Button isIconOnly size="xsmall" variant="ghost">
                <IconInfoCircle />
              </Button>
            </Tooltip>
          </Flex>

          {/* View Mode Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant="soft" size="small" endIcon={<IconChevronDown />}>
                {viewMode === 'all-files' ? (
                  <>
                    <IconList title="Show all files" />
                  </>
                ) : (
                  <>
                    <IconBinaryTree title="Show form tree with files" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                asChild
                onClick={() => setViewMode('form-tree-with-files')}
              >
                <ListItem
                  startIcon={<IconBinaryTree />}
                  primary="Show form tree with files"
                  endIcon={
                    viewMode === 'form-tree-with-files' ? <IconCheck /> : null
                  }
                />
              </DropdownMenuItem>
              <DropdownMenuItem
                asChild
                onClick={() => setViewMode('all-files')}
              >
                <ListItem
                  startIcon={<IconList />}
                  primary="Show all files"
                  endIcon={
                    viewMode === 'all-files' ? <IconCheck size={16} /> : null
                  }
                />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div style={{ padding: '8px 12px' }}>
                <Flex align="center" gap="2" justify="between">
                  <Text size="2xsmall">Show only forms with imports</Text>
                  <Switch
                    size="small"
                    checked={showOnlyImportForms}
                    onCheckedChange={checked =>
                      setShowOnlyImportForms(checked === true)
                    }
                    onClick={e => e.stopPropagation()}
                  />
                </Flex>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </Flex>

        {/* Tree or Files View */}
        {viewMode === 'all-files' ? renderAllFilesView() : renderTreeView()}

        {/* Summary */}
        <VisuallyHidden>
          <Flex gap="2" wrap="wrap">
            <Badge color="blue" variant="soft">
              {forms.length} forms
            </Badge>
            <Badge color="green" variant="soft">
              {selectedFiles.length} files
            </Badge>
            <Badge color="orange" variant="soft">
              {fileToFormMappings.length} mapped
            </Badge>
          </Flex>
        </VisuallyHidden>
      </Flex>
    </ScrollArea>
  );
};

export default FormHierarchyPreviewTree;
